import React from 'react';
import { Calendar, Clock, User, Star, CheckCircle, XCircle, ArrowRight, Heart, Lightbulb, TrendingUp, Shield, Zap, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const TestJiminiPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Jimini AI : Test Complet de l'IA Juridique Française"
      : "Jimini AI: Complete Review of the French Legal AI",
    "datePublished": "2026-01-18T10:00:00+01:00",
    "author": { "@type": "Organization", "name": "Gaius Legal AI" }
  };

  const seoTitle = language === 'fr' 
    ? "Jimini AI Avocat 2026 : Test Complet (Avis, RGPD, Prix)"
    : "Jimini AI 2026: Complete Review for French Lawyers";

  const seoDescription = language === 'fr'
    ? "Test approfondi de Jimini AI, l'IA juridique française de référence. Comparatif avec Harvey, Doctrine et Lexis Nexis IA. Notre coup de cœur pour le droit français."
    : "In-depth review of Jimini AI, the leading French legal AI. Comparison with Harvey, Doctrine and Lexis Nexis AI. Our top pick for French law.";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="jimini ai, jimini avocat, ia juridique française, ia générative avocats, jimini vs harvey, jimini vs doctrine, intelligence artificielle droit français"
          canonicalUrl="https://gaius-tech.com/articles/test-jimini"
          alternateUrlFr="https://gaius-tech.com/articles/test-jimini"
          alternateUrlEn="https://gaius-tech.com/articles/test-jimini"
          structuredData={structuredData}
          pageType="article"
        />
        
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">AI News</Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-800 flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  Coup de cœur
                </Badge>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Jimini AI pour Avocats : Test Complet 2026 de l'IA Juridique Française (RGPD, Prix, Avis)
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Notre coup de cœur 2026 pour les avocats et juristes français. Test complet, fonctionnalités, et comparatif avec Harvey et Doctrine.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>18 janvier 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>16 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1200&h=400&fit=crop"
              alt="Jimini AI - IA juridique française"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-xl leading-relaxed mb-8">
              Dans l'écosystème foisonnant des outils d'<strong>IA générative pour avocats</strong>, Jimini AI occupe une place à part. Cette solution française, développée spécifiquement pour le droit continental, offre aux praticiens une alternative crédible aux géants américains. Après plusieurs semaines de test intensif, nous vous livrons notre analyse complète — et notre coup de cœur.
            </p>

            <div className="bg-red-50 border-l-4 border-red-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Heart className="w-6 h-6 text-red-500 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-red-800 font-bold text-lg mb-2">Pourquoi c'est notre coup de cœur</h4>
                  <p className="text-red-700 mb-0">
                    Jimini AI combine une expertise approfondie du droit français, des sources vérifiables, un tarif accessible et une conformité RGPD native. Pour un avocat français, c'est aujourd'hui le meilleur rapport qualité/pertinence/prix du marché.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Qu'est-ce que Jimini AI ? L'IA conçue pour le droit français
            </h2>

            <p className="mb-6">
              Jimini AI est une startup française fondée en 2023, avec une ambition claire : créer l'<strong>intelligence artificielle juridique</strong> de référence pour le droit français et européen. Contrairement aux solutions américaines comme Harvey, Jimini a été pensé dès l'origine pour les spécificités du système juridique continental.
            </p>

            <p className="mb-6">
              L'équipe fondatrice réunit des avocats d'affaires issus des plus grands cabinets parisiens et des ingénieurs spécialisés en NLP (Natural Language Processing). Cette double compétence se ressent dans le produit : Jimini « parle avocat » et comprend les subtilités du raisonnement juridique français.
            </p>

            <p className="mb-8">
              Le modèle a été entraîné sur un corpus massif de jurisprudence française (Cour de cassation, Conseil d'État, cours d'appel), de doctrine et de textes législatifs. Cette spécialisation fait toute la différence dans la qualité des réponses.
            </p>

            <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <TrendingUp className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-bold text-lg mb-2">Jimini AI en chiffres</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• Plus de 500 cabinets utilisateurs en France</li>
                    <li>• 2 millions+ de décisions françaises indexées</li>
                    <li>• Temps de réponse moyen : 8 secondes</li>
                    <li>• Taux de satisfaction utilisateurs : 94%</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Test Jimini AI : fonctionnalités clés pour avocats français
            </h2>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              1. Recherche juridique avec sources vérifiables
            </h3>

            <p className="mb-6">
              C'est LA fonctionnalité qui distingue Jimini des IA généralistes. Chaque réponse est accompagnée de citations précises avec liens directs vers les décisions originales. Plus de doute sur l'existence d'une jurisprudence : vous pouvez vérifier en un clic.
            </p>

            <p className="mb-8">
              <strong>Notre test :</strong> Sur une question complexe de droit social (requalification de contrat de prestation en contrat de travail), Jimini a produit une analyse en 3 parties citant 12 arrêts de la Cour de cassation, tous vérifiables sur Légifrance. Aucune hallucination détectée.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              2. Analyse de documents et extraction d'informations
            </h3>

            <p className="mb-6">
              Jimini peut analyser vos documents (contrats, statuts, procès-verbaux) et en extraire les informations clés. L'outil identifie les clauses potentiellement problématiques et suggère des améliorations conformes à la pratique française.
            </p>

            <p className="mb-8">
              Nous avons testé l'analyse d'un pacte d'actionnaires de 45 pages. En moins de 5 minutes, Jimini a produit une fiche de synthèse complète identifiant les droits de chaque partie, les clauses de sortie, les conditions de cession et les points nécessitant une attention particulière.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              3. Rédaction assistée adaptée au style français
            </h3>

            <p className="mb-6">
              Jimini génère des projets de documents dans un style parfaitement adapté à la pratique juridique française : vocabulaire approprié, formules consacrées, structure conforme aux usages. Fini les traductions maladroites de modèles anglo-saxons.
            </p>

            <p className="mb-8">
              Particulièrement utile pour : conclusions, assignations, notes juridiques, clauses contractuelles, statuts de sociétés, PV d'assemblées.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              4. Veille juridique personnalisée
            </h3>

            <p className="mb-8">
              Jimini propose un système d'alertes sur les évolutions jurisprudentielles et législatives dans vos domaines de pratique. Une fonctionnalité précieuse pour rester à jour sans effort.
            </p>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Jimini AI vs Harvey AI vs Doctrine : comparatif IA juridique 2026
            </h2>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden">
                <thead className="bg-secondary">
                  <tr>
                    <th className="p-4 text-left font-bold">Critère</th>
                    <th className="p-4 text-left font-bold">Jimini AI</th>
                    <th className="p-4 text-left font-bold">Harvey</th>
                    <th className="p-4 text-left font-bold">Doctrine</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Spécialisation droit FR</td>
                    <td className="p-4">⭐⭐⭐⭐⭐</td>
                    <td className="p-4">⭐⭐</td>
                    <td className="p-4">⭐⭐⭐⭐⭐</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">IA générative</td>
                    <td className="p-4">⭐⭐⭐⭐</td>
                    <td className="p-4">⭐⭐⭐⭐⭐</td>
                    <td className="p-4">⭐⭐⭐</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Sources vérifiables</td>
                    <td className="p-4">✅ Oui</td>
                    <td className="p-4">⚠️ Partiel</td>
                    <td className="p-4">✅ Oui</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">Tarif accessible</td>
                    <td className="p-4">✅ Oui</td>
                    <td className="p-4">❌ Non</td>
                    <td className="p-4">✅ Oui</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Conformité RGPD</td>
                    <td className="p-4">✅ Native</td>
                    <td className="p-4">✅ Oui</td>
                    <td className="p-4">✅ Native</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Avis avocats : les points forts de Jimini AI
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Expertise droit français inégalée</h4>
                  <p className="text-muted-foreground mt-1">
                    Le modèle a été entraîné spécifiquement sur la jurisprudence et la doctrine françaises. La qualité des réponses sur le droit national est nettement supérieure aux solutions américaines.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Sources toujours vérifiables</h4>
                  <p className="text-muted-foreground mt-1">
                    Chaque citation inclut un lien direct vers la source. Vous pouvez vérifier en un clic et intégrer les références dans vos conclusions ou notes.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Tarification accessible</h4>
                  <p className="text-muted-foreground mt-1">
                    Jimini propose des offres adaptées à toutes les tailles de cabinet, de l'avocat individuel au grand cabinet. L'investissement est rentabilisé dès les premières utilisations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Conformité RGPD native</h4>
                  <p className="text-muted-foreground mt-1">
                    Hébergement en Europe, données protégées, pas de transfert hors UE. Vous pouvez utiliser Jimini sur vos dossiers confidentiels en toute sérénité.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Limites de Jimini AI : couverture internationale et intégrations
            </h2>

            <div className="space-y-6 my-8">
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Couverture internationale limitée</h4>
                  <p className="text-muted-foreground mt-1">
                    Jimini excelle sur le droit français mais reste moins pertinent pour le droit anglo-saxon ou international. Pour les dossiers cross-border, combinez avec d'autres outils.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Écosystème d'intégrations en développement</h4>
                  <p className="text-muted-foreground mt-1">
                    Les intégrations avec les outils tiers (DMS, Microsoft 365) sont moins avancées que chez les concurrents américains. L'équipe travaille activement sur ce point.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Lightbulb className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-bold text-lg mb-2">Notre recommandation</h4>
                  <p className="text-amber-700 mb-0">
                    Jimini AI est notre recommandation n°1 pour tout praticien exerçant principalement en droit français. La combinaison expertise locale + sources vérifiables + tarif accessible en fait l'outil idéal pour les cabinets français de toutes tailles.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Notre verdict Jimini AI 2026 : note 5/5, coup de cœur pour avocats français
            </h2>

            <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
              ))}
              <span className="ml-4 text-2xl font-bold">5/5</span>
            </div>

            <p className="mb-6">
              Jimini AI mérite sa note maximale pour plusieurs raisons : c'est l'outil le plus pertinent pour le droit français, les sources sont systématiquement vérifiables, le tarif est accessible et la conformité RGPD est native.
            </p>

            <p className="mb-8">
              Pour un avocat ou juriste français, Jimini devrait être le premier outil d'<strong>IA générative</strong> à adopter. C'est notre coup de cœur absolu.
            </p>

            <div className="bg-primary/5 rounded-xl p-8 my-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">Récapitulatif Jimini AI</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-green-700 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" /> Points forts
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Spécialisation droit français</li>
                    <li>✓ Sources toujours vérifiables</li>
                    <li>✓ Tarification accessible</li>
                    <li>✓ RGPD natif</li>
                    <li>✓ Interface intuitive</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-700 mb-3 flex items-center">
                    <XCircle className="w-5 h-5 mr-2" /> Limitations
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✗ Couverture internationale limitée</li>
                    <li>✗ Intégrations en développement</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>Idéal pour :</strong> Avocats français, juristes d'entreprise, cabinets de toutes tailles, contentieux, conseil, droit des affaires.
                </p>
              </div>
            </div>

          </div>

          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Zap className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Fondamentaux de l'IA Juridique</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Maîtrisez les outils d'IA juridique pour optimiser votre pratique quotidienne.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      Découvrir <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Database className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation IA Générative Avocats</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Comparez et maîtrisez tous les outils d'IA juridique du marché.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="jimini ai, french legal ai, generative ai lawyers, artificial intelligence french law"
        canonicalUrl="https://gaius-tech.com/articles/test-jimini"
        alternateUrlFr="https://gaius-tech.com/articles/test-jimini"
        alternateUrlEn="https://gaius-tech.com/articles/test-jimini"
        structuredData={structuredData}
        pageType="article"
      />
      
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">AI News</Badge>
              <Badge variant="secondary" className="bg-red-100 text-red-800 flex items-center gap-1">
                <Heart className="w-3 h-3" />
                Our Pick
              </Badge>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Jimini AI for Lawyers: Complete 2026 Review of the French Legal AI (GDPR, Pricing, Verdict)
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Our 2026 top pick for French lawyers and legal professionals. Complete review, features, and comparison with Harvey and Doctrine.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-xl leading-relaxed mb-8">
            In the crowded ecosystem of <strong>generative AI for lawyers</strong>, Jimini AI stands apart. This French solution, specifically developed for continental law, offers practitioners a credible alternative to American giants.
          </p>

          <div className="bg-red-50 border-l-4 border-red-400 p-6 my-10 rounded-r-lg">
            <div className="flex items-start">
              <Heart className="w-6 h-6 text-red-500 mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-bold text-lg mb-2">Why It's Our Top Pick</h4>
                <p className="text-red-700 mb-0">
                  Jimini AI combines deep French law expertise, verifiable sources, accessible pricing and native GDPR compliance. For French lawyers, it's the best value proposition on the market.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Key Features That Make the Difference
          </h2>

          <p className="mb-8">
            Jimini's standout feature is verifiable sources: every response includes precise citations with direct links to original decisions. No more doubt about case law existence—verify with one click.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Our Final Verdict
          </h2>

          <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
            <span className="ml-4 text-2xl font-bold">5/5</span>
          </div>

          <p className="mb-8">
            Jimini AI deserves its maximum rating: most relevant for French law, systematically verifiable sources, accessible pricing and native GDPR compliance. For French lawyers, this should be the first <strong>generative AI</strong> tool to adopt.
          </p>

        </div>

        <div className="mt-16 pt-8 border-t">
          <Button asChild>
            <Link to="/training">
              Discover our training programs <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
